<template>
  <div id="top" class="product-overview col-12 col-md-8 col-lg-9" role="main">
    <div
      v-if="catalog.breadcrumbs && catalog.breadcrumbs.length > 0"
      class="row d-none d-sm-none d-md-block d-lg-block"
    >
      <nav aria-label="breadcrumb" class="catalog-overview-breadcrumbs">
        <ol class="breadcrumb">
          <li
            v-for="(item, index) in catalog.breadcrumbs"
            :key="index"
            class="breadcrumb-item"
            :class="{ active: isLastItem(index) }"
          >
            <a v-if="!isLastItem(index)" :href="item.url">{{ item.name }}</a>
            <span v-else>{{ item.name }}</span>
          </li>
        </ol>
      </nav>
    </div>

    <!-- Title -->
    <div class="row">
      <div
        :class="{ 'col-12': true, 'text-center': catalog.pagetype == 'image' }"
      >
        <h1 class="product-overview-title mb-4">
          {{ catalog.title }}
        </h1>
      </div>
    </div>

    <div v-if="catalog.intro" :class="{ row: true, 'mb-4': !!catalog.image }">
      <!-- Header image -->
      <div
        v-if="catalog.image"
        class="col-lg-4 col-xl-3 d-none d-sm-none d-md-none d-lg-block"
      >
        <div
          class="cat-header-img"
          :style="{
            'background-image': `url('${catalog.image}')`,
          }"
        ></div>
      </div>

      <div
        :class="{
          'col-12': true,
          'col-lg-8': !!catalog.image,
          'col-xl-9': !!catalog.image,
        }"
      >
        <!-- Intro text -->
        <div v-if="catalog.intro" class="row">
          <div class="col-12">
            <!-- eslint-disable vue/no-v-html -->
            <div
              ref="intro-text"
              class="intro-text-wrapper pb-3"
              v-html="catalog.intro"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
            <p v-if="catalog.store_url">
              <a :href="affiliateOutlink" target="_blank"
                >{{ showStoreUrl(catalog.store_url) }}
                <IconExternalLink class="mb-2 ml-2 green"
              /></a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Subcategory filters -->
    <div
      v-if="catalog.subcategoryFilters && catalog.subcategoryFilters.length > 0"
      class="row mt-md-3 mb-3 mb-md-4"
    >
      <div class="col mb-md-3 d-flex">
        <button
          v-for="filter in catalog.subcategoryFilters"
          :key="filter.slug"
          :class="{
            'btn me-3': true,
            'btn-outline-secondary': activeSubcategoryFilterSlug != filter.slug,
            'btn-secondary': activeSubcategoryFilterSlug == filter.slug,
          }"
          @click="
            $emit(
              'setSubcategoryFilter',
              activeSubcategoryFilterSlug == filter.slug ? null : filter.slug
            )
          "
        >
          {{ filter.title }}
        </button>
      </div>
    </div>

    <!-- Subcategory suggestions -->
    <div
      v-if="
        (!catalog.subcategoryFilters ||
          catalog.subcategoryFilters.length === 0) &&
        catalog.subcategorySuggestions &&
        catalog.subcategorySuggestions.length > 0
      "
      class="row"
    >
      <div class="col subcategory-suggestions-col mb-3">
        <a
          v-for="suggestion in catalog.subcategorySuggestions"
          :key="suggestion.url"
          :href="suggestion.url"
          :class="{
            'btn me-2 mb-2': true,
            'btn-outline-secondary':
              suggestion.url != selectedSubcategorySuggestion,
            'btn-secondary': suggestion.url == selectedSubcategorySuggestion,
          }"
          @click.prevent="onSubcategorySuggestionClick"
        >
          {{ suggestion.title }}
        </a>
      </div>
    </div>

    <!-- /all/ category suggestions -->
    <div v-if="$route.params.category === 'all'" class="row">
      <div class="col subcategory-suggestions-col mb-3">
        <router-link
          v-for="category in categories"
          :key="category.id"
          :to="`/catalog/${category.slug}/`"
          class="btn btn-outline-secondary me-2 mb-2"
        >
          {{ category.name }}
        </router-link>
      </div>
    </div>

    <hr class="mt-0 mb-4" />

    <div class="row justify-content-between mb-4 d-none d-md-flex">
      <!-- Number of products -->
      <div class="col">
        <ais-stats>
          <template #default="{ nbHits }">
            {{ nbHits }}
            {{ nbHits === 1 ? gettext('Product') : gettext('Producten') }}
          </template>
        </ais-stats>
      </div>

      <!-- Number of items per page -->
      <div
        v-if="
          !$store.state.enableInfinitescrollABtest ||
          $store.state.infinitescrollABtest === 'A'
        "
        class="col text-center items-per-page"
      >
        <ItemsPerPageDropdown />
      </div>

      <!-- Sorting options -->
      <div class="col text-end">
        <SortByDropdown />
      </div>
    </div>

    <div class="catalog-wrapper">
      <!-- Categories and filters buttons -->
      <div
        class="mobile-action-buttons row justify-content-between mb-3 d-md-none"
      >
        <div class="col d-md-none pe-2">
          <button
            class="btn btn-primary w-100"
            @click="() => openMobileSidebarButton('catalog-categories')"
          >
            <span>{{ gettext('Categorieën') }}</span>
            <icon-chevron-right class="white" />
          </button>
        </div>
        <div class="col d-md-none pl-2">
          <button
            class="btn btn-primary w-100"
            @click="$emit('openFilterSidebar')"
          >
            <span>{{ gettext('Filteren & sorteren') }}</span>
            <icon-chevron-right class="white" />
          </button>
        </div>
      </div>

      <CurrentRefinements />

      <!-- No results text -->
      <ais-state-results>
        <template #default="{ state, results: { hits } }">
          <div v-show="!hits.length" class="py-6 text-center">
            <p v-show="!hits.length && state.query" class="lead">
              {{ gettext('Geen resultaten voor zoekopdracht:') }}
              {{ state.query }}
            </p>
            <p v-show="!hits.length && !state.query" class="lead">
              {{
                gettext(
                  'Geen resultaten. Verwijderd filters om meer resultaten te weergeven.'
                )
              }}
            </p>
          </div>
        </template>
      </ais-state-results>

      <!-- Products -->
      <ais-hits
        v-if="
          !$store.state.enableInfinitescrollABtest ||
          $store.state.infinitescrollABtest === 'A'
        "
        :class-names="{
          'ais-Hits': `row mx-n2 pt-2 ${
            disableShadows ? 'disable-shadows' : ''
          }`,
        }"
      >
        <template #default="{ items, sendEvent }">
          <div
            v-for="(item, index) in items"
            :key="item.objectID"
            class="col-6 col-lg-4 px-1 px-md-2 mb-2 mb-md-3"
          >
            <ProductView
              :object-id="item.objectID"
              :product-id="item.product_id"
              :slug="item.slug"
              :name="item.title"
              :store="item.store_name"
              :brand="item.brand_name"
              :image="item.main_image_url"
              :images="{
                default: {
                  default: item.main_image_urls_default_default,
                  webp: item.main_image_urls_default_webp,
                },
                big: {
                  default: item.main_image_urls_big_default,
                  webp: item.main_image_urls_big_webp,
                },
              }"
              :image-aspect-ratio="item.main_image_aspect_ratio"
              :image-show-full="item.full_image_flag"
              :currency="$store.state.currency"
              :price="item[`price_${$store.state.currency.toLowerCase()}`]"
              :old-price="
                item[`price_old_${$store.state.currency.toLowerCase()}`]
              "
              :is-discounted="item.discounted"
              :has-giftcard="item.fair_fashion_giftcard"
              :is-lazy-loading="index > 2"
              :labels="item.label_ids"
              :send-event="sendEvent"
              :item="item"
            />
          </div>
        </template>
      </ais-hits>

      <infinite-hits
        v-else-if="
          $store.state.enableInfinitescrollABtest &&
          $store.state.infinitescrollABtest === 'B'
        "
        :show-previous="true"
        :transform-items="transformItems"
        @startLoadingNextPage="onStartLoadingNextPage"
      >
        <template #loadPrevious="{ isFirstPage }">
          <div class="row">
            <div class="col d-flex justify-content-center align-items-center">
              <a
                v-if="!isFirstPage"
                :href="updateUrl({ remove: ['page'] })"
                class="first-page-button btn btn-light bg-grey-light mb-2 mb-md-4"
                @click.prevent="navigateToFirstPage"
              >
                {{ gettext('👈 Show first page') }}
              </a>
            </div>
          </div>
        </template>
        <template #default="{ items, sendEvent }">
          <div
            :class="`row mx-n2 pt-2 ${disableShadows ? 'disable-shadows' : ''}`"
          >
            <div
              v-for="(item, index) in items"
              :key="item.objectID"
              class="col-6 col-lg-4 px-1 px-md-2 mb-2 mb-md-3"
            >
              <ProductView
                :object-id="item.objectID"
                :product-id="item.product_id"
                :slug="item.slug"
                :name="item.title"
                :store="item.store_name"
                :brand="item.brand_name"
                :image="item.main_image_url"
                :images="{
                  default: {
                    default: item.main_image_urls_default_default,
                    webp: item.main_image_urls_default_webp,
                  },
                  big: {
                    default: item.main_image_urls_big_default,
                    webp: item.main_image_urls_big_webp,
                  },
                }"
                :image-aspect-ratio="item.main_image_aspect_ratio"
                :image-show-full="item.full_image_flag"
                :currency="$store.state.currency"
                :price="item[`price_${$store.state.currency.toLowerCase()}`]"
                :old-price="
                  item[`price_old_${$store.state.currency.toLowerCase()}`]
                "
                :is-discounted="item.discounted"
                :has-giftcard="item.fair_fashion_giftcard"
                :is-lazy-loading="index > 2"
                :labels="item.label_ids"
                :send-event="sendEvent"
                :item="item"
              />
            </div>
          </div>
        </template>
        <template #loadMore="{ isLastPage, page, refineNext }">
          <div class="col d-flex justify-content-center align-items-center">
            <IconLoading v-if="isLoadingNextPage" class="mb-4" />
            <a
              v-else-if="!isLastPage"
              :href="updateUrl({ add: { page: page + 2 } })"
              class="btn btn-primary mb-4"
              @click.prevent="refineNext"
            >
              {{ gettext('Show more results') }}
            </a>
          </div>
        </template>
      </infinite-hits>

      <!-- Pagination -->
      <CatalogPagination
        v-if="
          !$store.state.enableInfinitescrollABtest ||
          $store.state.infinitescrollABtest === 'A'
        "
      />
    </div>

    <div v-if="catalog.footer" :class="{ row: true, 'mb-4': !!catalog.image }">
      <div
        :class="{
          'col-12': true,
        }"
      >
        <!-- Intro text -->
        <div v-if="catalog.footer" class="row">
          <div class="col-12">
            <!-- eslint-disable vue/no-v-html -->
            <div
              ref="footer-text"
              class="mb-4 mt-6 intro-text-wrapper footer-text"
              v-html="catalog.footer"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AisStats, AisStateResults, AisHits } from 'vue-instantsearch';
import InfiniteHits from './InfiniteHits.vue';
import CurrentRefinements from './CurrentRefinements.vue';
import ProductView from './ProductView.vue';
import ItemsPerPageDropdown from './ItemsPerPageDropdown.vue';
import SortByDropdown from './SortByDropdown.vue';
import CatalogPagination from './CatalogPagination.vue';
import { updateUrlQuery } from '../util/url';
import IconChevronRight from '../../../../templates/icons/chevron-right.svg';
import IconLoading from '../../../../templates/icons/loading.svg';
import IconExternalLink from '../../../../templates/icons/external-link.svg';

export default {
  components: {
    AisStats,
    AisStateResults,
    AisHits,
    InfiniteHits,
    CurrentRefinements,
    ProductView,
    ItemsPerPageDropdown,
    SortByDropdown,
    CatalogPagination,
    IconChevronRight,
    IconLoading,
    IconExternalLink,
  },
  props: {
    subcategoryFilterSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      activeSubcategoryFilterSlug: this.subcategoryFilterSlug,
      selectedSubcategorySuggestion: null,
      isLoadingNextPage: false,
      section:
        this.$store.getters.sectionSlugToIdMap[this.$route.params.section],
      // section: this.$route.params.section,
      // Temporarily: should be removed when a decision has been
      // made about the productview shadows
      disableShadows: this.$route.query.disableShadows || false,
    };
  },
  computed: {
    catalog() {
      return this.$store.state.catalog;
    },
    affiliateOutlink() {
      if (!this.$store.state.catalog.store_url) {
        return '';
      }
      return this.$store.getters.getAffiliateOutlink(
        this.$store.state.catalog.store_url
      );
    },
    categories() {
      // Category buttons for the 'All' catalog pages
      if (!this.$store.state.categories[this.section]) {
        return [];
      }
      const categories = Object.keys(
        this.$store.state.categories[this.section].c
      );
      console.log('categoriess');
      console.log(categories);
      return categories
        .map(id => ({
          id,
          name: this.$store.state.categories[this.section].c[id].name,
          slug: this.$store.state.categories[this.section].c[id].slug,
        }))
        .sort((a, b) => categories.indexOf(b.id) - categories.indexOf(a.id))
        .slice(0, 13);
    },
  },
  watch: {
    $route(to) {
      // On page change see if the current page is still
      // the selected subcategory suggestion
      if (to.path !== this.selectedSubcategorySuggestion) {
        this.selectedSubcategorySuggestion = null;
      }
    },
    subcategoryFilterSlug(newVal) {
      if (newVal != this.activeSubcategoryFilterSlug) {
        this.activeSubcategoryFilterSlug = newVal;
      }
    },
  },
  mounted() {
    this.bindIntroTextClicks();
  },
  beforeDestroy: function () {
    this.unbindIntroTextClicks();
  },
  methods: {
    isLastItem(index) {
      return index === this.catalog.breadcrumbs.length - 1;
    },
    openMobileSidebarButton(sidebar) {
      window.openMobileSidebarButton(sidebar);
    },
    updateUrl({ add, remove }) {
      return updateUrlQuery({ path: this.$route.fullPath, add, remove });
    },
    navigateToFirstPage() {
      // Remove the page parameter from the url and navigate there
      this.$router.push(this.updateUrl({ remove: ['page'] }));
    },
    transformItems(items) {
      // Assume that when transformItems gets called the next batch of products
      // has been loaded. Give it a little time out so that the new results,
      // are first rendered before we remove the loading animation
      if (typeof window === 'object') {
        setTimeout(() => {
          this.isLoadingNextPage = false;
        }, 500);
      }
      return items;
    },
    onStartLoadingNextPage() {
      if (typeof window === 'object') {
        this.isLoadingNextPage = true;
      }
    },
    /**
     * Make sure click on <a> elements in intro text go intro
     * vue-router instead of causing a whole page reload
     */
    bindIntroTextClicks() {
      if (!this.$refs['intro-text']) return;
      const aElms = this.$refs['intro-text'].querySelectorAll('a');
      aElms.forEach(elm => {
        elm.addEventListener('click', this.onIntroTextLinkClick);
      });
    },
    onIntroTextLinkClick(e) {
      const href = e.target.getAttribute('href');
      if (href.startsWith('/catalog')) {
        e.preventDefault();
        this.$router.push(href);
      }
    },
    onSubcategorySuggestionClick(e) {
      const subcategoryPath = e.target.getAttribute('href');

      // If visitor clicked on the current subcategory we deselect the
      // subcategory and go to the parent subcategory
      if (subcategoryPath === this.selectedSubcategorySuggestion) {
        this.selectedSubcategorySuggestion = null;
        // Remove the last part of the subcategory url to get the category url
        const categoryUrl =
          subcategoryPath.replace(/\/$/, '').split('/').slice(0, -1).join('/') +
          '/';
        // Go to the new url, but include the query parameters to remember the filters
        // except the 'page' query parameter
        this.$router.push(
          updateUrlQuery({
            path: categoryUrl + window.location.search,
            remove: ['page'],
          })
        );
        return;
      }

      // Otherwise go to the subcategory, if we're not there yet
      this.selectedSubcategorySuggestion = subcategoryPath;
      if (subcategoryPath !== this.$route.path) {
        // Remove the 'page' query parameter, start from page 1
        const subcategoryUrl = updateUrlQuery({
          path: subcategoryPath + window.location.search,
          remove: ['page'],
        });
        this.$router.push(subcategoryUrl);
      }
    },
    unbindIntroTextClicks() {
      if (!this.$refs['intro-text']) return;
      const aElms = this.$refs['intro-text'].querySelectorAll('a');
      aElms.forEach(elm => {
        elm.removeEventListener('click', this.onIntroTextLinkClick);
      });
    },
    showStoreUrl(url) {
      url = url.split('//')[1];

      if (url.indexOf('www.') > -1) {
        url = url.split('www.')[1];
      }

      if (url.endsWith('/')) {
        url = url.split('/')[0];
      }
      return url;
    },
  },
};
</script>
